import { useQueryClient } from "@tanstack/react-query";
import { ChevronRight, FileWarning, Plus } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import {
  useAssignDocumentToPatient,
  useReprocessDocument,
} from "@elation/api-client/src/stitch-web";
import type {
  Patient,
  PatientSearchItem,
  UnmatchedPatientUpload,
} from "@elation/api-client/src/stitch-web/models";

import { DocumentEntity, DocumentViewer } from "@src/features/documents";
import { useResolvePatientId } from "@src/features/patients";
import {
  CreatePatientPopoverV2,
  PatientSearch,
} from "@src/features/patients/v2";
import { useIsPhysician } from "@src/features/physicians";
import { VerticalFlexbox } from "@src/shared/components/layout/Flexbox";
import { Button } from "@src/shared/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@src/shared/components/ui/collapsible";
import { getDisplayErrorMessage } from "@src/shared/utils/errors";

import { CareProtocolOpportunityEntity } from "../../../../../../domain";
import { UploadHistoryFileNameTrigger } from "../UploadHistoryFileNameTrigger";

export interface NeedsReviewPatientUploadCardProps {
  item: UnmatchedPatientUpload;
}

export function NeedsReviewPatientUploadCard({
  item,
}: NeedsReviewPatientUploadCardProps) {
  const canCreatePatient = useIsPhysician();
  const queryClient = useQueryClient();
  const assignDocument = useAssignDocumentToPatient();
  const reprocessDocument = useReprocessDocument();
  const { isResolvingPatientId, resolvePatientId } = useResolvePatientId();
  const [isOpen, setIsOpen] = useState(false);
  const [isCreatingPatient, setIsCreatingPatient] = useState(false);
  const isAssigning =
    assignDocument.isPending ||
    reprocessDocument.isPending ||
    isResolvingPatientId ||
    isCreatingPatient;

  const refreshUploadHistory = async () => {
    await queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey[0];
        return (
          key === "/gw/stitch-web/api/documents/unmatched-patient-uploads" ||
          key === "/gw/stitch-web/api/documents/care-gap-uploads" ||
          key === "/gw/stitch-web/api/care-protocol-opportunities"
        );
      },
    });
  };

  const assignAndReprocessDocument = async (patientId: number) => {
    await assignDocument.mutateAsync({
      id: item.document.id,
      data: { patientId },
    });
    try {
      await reprocessDocument.mutateAsync({ id: item.document.id });
      await refreshUploadHistory();
    } catch (err) {
      await assignDocument.mutateAsync({
        id: item.document.id,
        data: { patientId: null },
      });
      await refreshUploadHistory();
      throw err;
    }
  };

  const assignSearchResult = async (patient: PatientSearchItem) => {
    try {
      const patientId = await resolvePatientId(patient);
      await assignAndReprocessDocument(patientId);
      setIsOpen(false);
    } catch (err) {
      toast.error(
        getDisplayErrorMessage(err, {
          fallback: "Unable to assign this document. Please try again.",
        }),
      );
      throw err;
    }
  };

  const assignCreatedPatient = async (patient: Patient | null) => {
    if (patient?.id == null) return;

    await assignAndReprocessDocument(patient.id);
    setIsOpen(false);
  };

  return (
    <li key={item.document.id}>
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <div className="grid grid-cols-[minmax(150px,1.15fr)_minmax(160px,1.1fr)_120px_80px_72px] items-center gap-3 px-3 py-2.5">
          <CollapsibleTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              disabled={isAssigning}
              className="h-auto min-w-0 justify-start gap-2 rounded-[8px] px-0 py-0 text-left font-normal shadow-none hover:bg-transparent hover:text-foreground"
            >
              <ChevronRight
                className={`size-4 shrink-0 text-muted-foreground transition-transform ${isOpen ? "rotate-90" : ""}`}
                aria-hidden="true"
              />
              <div className="flex size-[28px] shrink-0 items-center justify-center rounded-[8px] bg-destructive/10 text-destructive">
                <FileWarning className="size-[14px]" aria-hidden="true" />
              </div>
              <VerticalFlexbox className="min-w-0 flex-1">
                <span className="truncate text-[13px] font-semibold text-primary underline-offset-2 hover:underline">
                  Select Patient
                </span>
                <span className="mt-0.5 truncate font-mono text-[10px] text-muted-foreground">
                  {DocumentEntity.needsReviewIdentifierSummaryLabel(
                    item.identifierSummary,
                  )}
                </span>
              </VerticalFlexbox>
            </Button>
          </CollapsibleTrigger>

          <VerticalFlexbox className="min-w-0" gap="none">
            <DocumentViewer
              documentId={item.document.id}
              fileName={item.document.fileName}
              uploadedAtLabel={CareProtocolOpportunityEntity.uploadTimeLabel(
                item.completedAt,
              )}
              triggerTooltip={item.document.fileName}
              trigger={
                <UploadHistoryFileNameTrigger
                  fileName={item.document.fileName}
                />
              }
            />
            <span className="mt-0.5 truncate font-mono text-[10px] text-muted-foreground">
              {DocumentEntity.needsReviewReasonLabel(item.reason)}
            </span>
          </VerticalFlexbox>
          <span className="font-mono text-[10.5px] text-muted-foreground">
            {CareProtocolOpportunityEntity.uploadTimeLabel(item.completedAt)}
          </span>
          <span className="font-mono text-[10.5px] text-muted-foreground">
            0 items
          </span>
          <span aria-hidden="true" />
        </div>
        <CollapsibleContent>
          <VerticalFlexbox className="gap-3 bg-muted/20 px-4 py-3">
            <div className="flex flex-col gap-2 md-stitch:flex-row md-stitch:items-start">
              <div className="md-stitch:w-[290px] md-stitch:shrink-0">
                <PatientSearch
                  autoFocus
                  canCreatePatient={false}
                  contentClassName="w-[min(calc(100vw-2rem),340px)]"
                  disabled={isAssigning}
                  inputClassName="h-8 rounded-[9px] text-base md-stitch:text-[13px]"
                  label={`Search patients for ${item.document.fileName}`}
                  listboxLabel={`Patient results for ${item.document.fileName}`}
                  navigateStateSource="upload history patient assignment"
                  onSelectPatient={assignSearchResult}
                  placeholder="Search patients"
                  selectingLabel="assigning..."
                />
              </div>
              {canCreatePatient ? (
                <CreatePatientPopoverV2
                  canCreatePatient={canCreatePatient}
                  onCreated={assignCreatedPatient}
                  onSubmittingChange={setIsCreatingPatient}
                  trigger={
                    <Button
                      type="button"
                      variant="ghost"
                      className="h-8 justify-start gap-1 rounded-[9px] px-2 text-[12px] font-semibold text-primary hover:bg-transparent hover:text-primary"
                      disabled={isAssigning}
                    >
                      <Plus className="size-3.5" aria-hidden="true" />
                      Create patient
                    </Button>
                  }
                />
              ) : null}
              <Button
                type="button"
                variant="outline"
                className="h-8 rounded-[9px] px-3 text-[12px] font-semibold"
                disabled={isAssigning}
                onClick={() => {
                  setIsOpen(false);
                }}
              >
                Cancel
              </Button>
            </div>
          </VerticalFlexbox>
        </CollapsibleContent>
      </Collapsible>
    </li>
  );
}
